/**
 * Estimate workspace — chat with Stella.
 *
 * Customers see the full chat panel (read + write). Tenant admins see
 * the same conversation read-only with a "(viewing as admin)" badge.
 * Cross-tenant or non-owned access redirects via the API's 404.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { AnchorRecorder } from "@/components/anchor/AnchorRecorder";
import type { ChatPanelHandle, PickerState } from "@/components/chat/ChatPanel";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { EstimateSnapshotPanel } from "@/components/chat/EstimateSnapshotPanel";
import { NextStepPanel } from "@/components/chat/NextStepPanel";
import { EstimateWizard } from "@/components/wizard/EstimateWizard";
import { ActionNeededBanner } from "@/components/estimate/ActionNeededBanner";
import { EstimateHeader } from "@/components/estimate/EstimateHeader";
import { IdentityEditor } from "@/components/estimate/IdentityEditor";
import { LifecyclePanel } from "@/components/estimate/LifecyclePanel";
import { FormError } from "@/components/FormError";
import { DimensionsPanel } from "@/components/photos/DimensionsPanel";
import { MeasurementsList } from "@/components/photos/MeasurementsList";
import { PhotoGallery } from "@/components/photos/PhotoGallery";
import { WallOverlayToggle } from "@/components/photos/WallOverlayToggle";
import { MobileWorkspaceDrawer } from "@/components/workspace/MobileWorkspaceDrawer";
import { ProjectDetailsCard } from "@/components/workspace/ProjectDetailsCard";
import { WorkspacePanel } from "@/components/workspace/WorkspacePanel";
import {
  approveDesign,
  approveRender,
  createDepositCheckout,
  getEstimate,
  getEstimateDesign,
  getEstimateSnapshot,
  listAnchors,
  listEstimateMessages,
  listEstimateSelections,
  rejectDesign,
  rejectRender,
} from "@starz/shared/api/sdk.gen";
import type {
  ChatMessage,
  Design,
  Estimate,
  EstimateStreamPayload,
  MaterialSelection,
  ScaleAnchor,
} from "@starz/shared/api/types.gen";
import { Lightbox } from "@/components/ui/Lightbox";
import { RendersGallery } from "@/components/photos/RendersGallery";
import { useAuthRole, useAuthUser } from "@/lib/auth/useAuth";
import { usePhotos } from "@starz/shared/photos/usePhotos";
import { summarizeDimensions } from "@starz/shared/wizard";

const OPENING_GREETING =
  "Hi — I'm Stella, your remodeling intake assistant. Tell me about the project you're considering, and I'll help capture the details so your contractor can prepare a thoughtful estimate. What space are you thinking about?";

export function EstimateWorkspace() {
  const { estimateId } = useParams<{ estimateId: string }>();
  const navigate = useNavigate();
  const role = useAuthRole();
  const authUser = useAuthUser();
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [snapshotPayload, setSnapshotPayload] = useState<EstimateStreamPayload | null>(null);
  const [initialPickers, setInitialPickers] = useState<PickerState[]>([]);
  const [initialDesign, setInitialDesign] = useState<Design | null>(null);
  const [anchors, setAnchors] = useState<ScaleAnchor[]>([]);
  const [selections, setSelections] = useState<MaterialSelection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingIdentity, setEditingIdentity] = useState(false);
  const [recordingAnchor, setRecordingAnchor] = useState(false);
  // Phase 18 — render lifecycle state.
  const [renderBusy, setRenderBusy] = useState(false);
  const [renderRefreshToken, setRenderRefreshToken] = useState(0);
  // Phase 18.4 — design approve/reject in-flight flag.
  const [designBusy, setDesignBusy] = useState(false);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const photosState = usePhotos(estimateId);
  // Phase 9 — imperative handle for the workspace's snapshot card to
  // trigger a synthetic chat turn ("ask for options").
  const chatRef = useRef<ChatPanelHandle | null>(null);

  useEffect(() => {
    if (!estimateId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [
        estimateResult,
        messagesResult,
        snapshotResult,
        selectionsResult,
        designResult,
        anchorsResult,
      ] = await Promise.all([
        getEstimate({ estimateId }),
        listEstimateMessages({ estimateId, limit: 200 }),
        getEstimateSnapshot({ estimateId }),
        listEstimateSelections({ estimateId }),
        getEstimateDesign({ estimateId }),
        listAnchors({ estimateId }),
      ]);
      if (cancelled) return;
      if ("error" in estimateResult) {
        setError(estimateResult.error.error.message);
        setLoading(false);
        return;
      }
      setEstimate(estimateResult.data);
      const loadedMessages = "data" in messagesResult ? messagesResult.data : [];
      setMessages(loadedMessages);
      if ("data" in snapshotResult) {
        // Wrap as a snapshot-kind EstimateStreamPayload so the existing
        // EstimateSnapshotPanel renders it identically to a live SSE
        // event.
        setSnapshotPayload({
          kind: "snapshot",
          snapshot: snapshotResult.data,
        });
      }
      const loadedSelections = "data" in selectionsResult ? selectionsResult.data : [];
      setSelections(loadedSelections);
      setInitialPickers(buildInitialPickers(loadedSelections, loadedMessages));
      if ("data" in designResult) {
        setInitialDesign(designResult.data);
      }
      if ("data" in anchorsResult) {
        setAnchors(anchorsResult.data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId]);

  // Phase 18.3 — refresh selections + anchors after every chat turn so
  // the NextStepPanel reflects live state (a material pick / anchor
  // save commits server-side; the panel listens here, not via Stella's
  // optional [[ESTIMATE]] recompute). Skips the first render so we
  // don't double-fetch what the loader just hydrated.
  const initialFetchSkippedRef = useRef(false);
  useEffect(() => {
    if (!estimateId || messages.length === 0) return;
    if (!initialFetchSkippedRef.current) {
      initialFetchSkippedRef.current = true;
      return;
    }
    let cancelled = false;
    void Promise.all([listEstimateSelections({ estimateId }), listAnchors({ estimateId })]).then(
      ([sel, a]) => {
        if (cancelled) return;
        if (!("error" in sel)) setSelections(sel.data);
        if (!("error" in a)) setAnchors(a.data);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [estimateId, messages.length]);

  // Phase 17 — poll the estimate every 30s so admin-side changes (a
  // new offer, an approval, a status flip from the expiry Lambda)
  // become visible without the customer needing to reload. Cheap: one
  // GetItem per tick. Stops while the page is hidden.
  useEffect(() => {
    if (!estimateId) return;
    let cancelled = false;
    const TICK_MS = 30_000;
    const id = window.setInterval(async () => {
      if (document.hidden) return;
      const result = await getEstimate({ estimateId });
      if (cancelled || "error" in result) return;
      setEstimate((prev) => {
        if (!prev) return result.data;
        // Avoid an identity churn when nothing changed — keeps child
        // components like ChatPanel from re-deriving needlessly.
        const fresh = result.data;
        if (
          prev.version === fresh.version &&
          prev.status === fresh.status &&
          prev.deposit_paid_at === fresh.deposit_paid_at
        ) {
          return prev;
        }
        return fresh;
      });
    }, TICK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [estimateId]);

  if (!estimateId) {
    return (
      <div className="mx-auto max-w-content px-12 py-16">
        <FormError message="No estimate id in URL." />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-content px-12 py-16">
        <p className="text-warm-gray">Loading conversation…</p>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="mx-auto max-w-content px-12 py-16">
        <h1 className="mb-4 font-serif text-3xl text-starz-navy">
          We couldn't open this estimate.
        </h1>
        <FormError message={error ?? "Estimate not found."} />
        <p className="mt-6 text-sm">
          <Link to="/dashboard" className="text-starz-gold underline">
            ← Back to dashboard
          </Link>
        </p>
      </div>
    );
  }

  const isAdminView = role === "tenant_admin";
  const photoCount = photosState.photos.length;
  const anchorCount = estimate.anchor_count ?? 0;

  // Phase 9 — workspace cards reused across desktop panel + mobile drawer.
  const overlayPhoto = photosState.photos[0];
  const overlayAnchor = anchors.find((a) => a.value_inches != null && a.value_inches > 0);
  const showOverlay = overlayPhoto && overlayAnchor && initialDesign;

  // Phase 20 — guided wizard completion signals, derived from the same
  // live state the "Almost there…" NextStepPanel reads.
  const snapshotReady = snapshotPayload != null && snapshotPayload.kind !== "missing_inputs";
  const capturedDimensions = summarizeDimensions(estimate.dimensions_rollup);
  const wizardSignals = {
    hasIntent: !!estimate.room_type,
    // A room scan populates the rollup even before an explicit anchor is
    // listed — count either signal as "dimensions captured".
    hasDimensions: anchors.length > 0 || capturedDimensions.length > 0,
    hasFinish: selections.length > 0,
    hasPhotos: photosState.photos.length > 0,
    hasEstimate: snapshotReady,
  };

  const workspaceCards = (
    <>
      <ProjectDetailsCard
        estimate={estimate}
        phone={authUser?.phone ?? null}
        snapshot={snapshotPayload?.snapshot ?? null}
      />
      <PhotoGallery
        estimateId={estimateId}
        photos={photosState.photos}
        needsScale={anchorCount === 0}
        readOnly={isAdminView}
        onUploaded={photosState.prepend}
        onDeleted={photosState.remove}
      />
      {!isAdminView && (
        <RendersGallery
          estimateId={estimateId}
          refreshToken={renderRefreshToken}
          onOpenLightbox={(src, alt) => setLightbox({ src, alt })}
          onAfterDelete={async () => {
            // Quota indicator lives on Estimate.renders_count; pull it.
            const eRes = await getEstimate({ estimateId });
            if (!("error" in eRes)) setEstimate(eRes.data);
          }}
        />
      )}
      {!isAdminView && anchorCount === 0 && !recordingAnchor && (
        <button
          type="button"
          onClick={() => setRecordingAnchor(true)}
          className="w-full rounded-lg border border-dashed border-starz-gold/50 bg-starz-cream/40 p-4 text-sm font-medium text-starz-navy hover:bg-starz-cream"
        >
          Record a scale anchor →
        </button>
      )}
      {recordingAnchor && (
        <div className="rounded-xl border border-starz-gold/30 bg-white p-5 shadow-sm">
          <AnchorRecorder
            estimateId={estimateId}
            onCancel={() => setRecordingAnchor(false)}
            onCreated={() => {
              setRecordingAnchor(false);
              setEstimate((prev) =>
                prev ? { ...prev, anchor_count: (prev.anchor_count ?? 0) + 1 } : prev,
              );
              void photosState.refresh();
            }}
          />
        </div>
      )}
      <DimensionsPanel rollup={estimate.dimensions_rollup} hasAnchor={anchorCount > 0} />
      <MeasurementsList
        anchors={anchors}
        estimateId={estimateId}
        canDelete={!isAdminView && estimate.status === "in_progress"}
        onDeleted={(anchorId) => {
          setAnchors((prev) => prev.filter((a) => a.anchor_id !== anchorId));
          setEstimate((prev) =>
            prev ? { ...prev, anchor_count: Math.max(0, (prev.anchor_count ?? 0) - 1) } : prev,
          );
        }}
      />
      {showOverlay && (
        <WallOverlayToggle photo={overlayPhoto} anchor={overlayAnchor} design={initialDesign} />
      )}
      {/* Phase 18.3 — reactive "Almost there…" panel. Auto-hides when
         all five steps are done; replaces the static missing_inputs
         branch of EstimateSnapshotPanel. */}
      {!isAdminView && (
        <NextStepPanel
          estimate={estimate}
          selections={selections}
          anchors={anchors}
          photos={photosState.photos}
          snapshot={snapshotPayload?.snapshot ?? null}
          onShowOptions={() => void chatRef.current?.askForOptions()}
          onAddMeasurement={() => void chatRef.current?.askForMeasurement()}
          onAddPhoto={() => void chatRef.current?.askForPhoto()}
          onCreateEstimate={() => void chatRef.current?.askForEstimate()}
        />
      )}
      {snapshotPayload && snapshotPayload.kind !== "missing_inputs" && (
        <EstimateSnapshotPanel payload={snapshotPayload} />
      )}
    </>
  );

  return (
    <div className="mx-auto max-w-content px-6 py-12 md:px-12">
      <EstimateHeader
        estimate={estimate}
        status={estimate.status}
        createdAt={estimate.created_at}
        isAdminView={isAdminView}
        onEdit={() => setEditingIdentity(true)}
      />
      {editingIdentity && (
        <div className="mb-6 rounded-xl border border-starz-gold/30 bg-white p-6 shadow-sm">
          <IdentityEditor
            estimate={estimate}
            onCancel={() => setEditingIdentity(false)}
            onSaved={(updated) => {
              setEstimate(updated);
              setEditingIdentity(false);
            }}
            onDeleted={
              isAdminView
                ? undefined
                : () => {
                    setEditingIdentity(false);
                    navigate("/dashboard");
                  }
            }
          />
        </div>
      )}
      <LifecyclePanel
        estimate={estimate}
        isAdminView={isAdminView}
        onSubmitted={(updated) => setEstimate(updated)}
      />
      <ActionNeededBanner show={!isAdminView && estimate.status === "info_requested"} />

      {/* Phase 9 — chat-primary two-column shell on desktop; chat is full
          width on mobile and the workspace cards live in a drawer. */}
      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_360px]">
        <EstimateWizard
          estimateId={estimateId}
          chatRef={chatRef}
          readOnly={isAdminView}
          spaceLabel={estimate.room_label || estimate.room_type}
          photoCount={photosState.photos.length}
          snapshotPayload={snapshotPayload}
          capturedDimensions={capturedDimensions}
          onPhotoUploaded={(photo) => photosState.prepend(photo)}
          onTurnSettled={async () => {
            // A chat turn just settled — refresh the signals the wizard
            // reads so anything Stella captured (a measurement/anchor, a
            // material pick, the estimate) completes the right step.
            const [a, sel, e, snap] = await Promise.all([
              listAnchors({ estimateId }),
              listEstimateSelections({ estimateId }),
              getEstimate({ estimateId }),
              getEstimateSnapshot({ estimateId }),
            ]);
            if (!("error" in a)) setAnchors(a.data);
            if (!("error" in sel)) setSelections(sel.data);
            if (!("error" in e)) setEstimate(e.data);
            if (!("error" in snap)) {
              setSnapshotPayload({ kind: "snapshot", snapshot: snap.data });
            }
          }}
          hasIntent={wizardSignals.hasIntent}
          hasDimensions={wizardSignals.hasDimensions}
          hasFinish={wizardSignals.hasFinish}
          hasPhotos={wizardSignals.hasPhotos}
          hasEstimate={wizardSignals.hasEstimate}
          renderChat={({ onConversationChange }) => (
            <ChatPanel
              ref={chatRef}
              onConversationChange={onConversationChange}
              estimateId={estimateId}
              initialMessages={messages}
              readOnly={isAdminView}
              emptyGreeting={isAdminView ? undefined : OPENING_GREETING}
              initialPickers={initialPickers}
              initialDesign={initialDesign}
              onDesignUpdated={(d) => setInitialDesign(d)}
              onSnapshotUpdated={(payload) => setSnapshotPayload(payload)}
              estimate={estimate}
              onPayDeposit={async (eid) => {
                const result = await createDepositCheckout({ estimateId: eid });
                if ("error" in result) {
                  setError(result.error.error.message);
                  return;
                }
                window.location.href = result.data.redirect_url;
              }}
              onRefreshed={async () => {
                // Re-fetch the estimate + messages so the fresh quote card
                // surfaces and the prior expired card stays in history.
                if (!estimateId) return;
                const [eRes, mRes] = await Promise.all([
                  getEstimate({ estimateId }),
                  listEstimateMessages({ estimateId }),
                ]);
                if (!("error" in eRes)) setEstimate(eRes.data);
                if (!("error" in mRes)) setMessages(mRes.data);
              }}
              renderBusy={renderBusy}
              onContinueProject={() =>
                window.scrollTo({ top: 0, behavior: "smooth" })
              }
              onApproveRender={async (renderId) => {
                if (!estimateId) return;
                setRenderBusy(true);
                const res = await approveRender({ estimateId, renderId });
                setRenderBusy(false);
                if (!("error" in res)) {
                  // Re-fetch messages + estimate so the in-chat card flips
                  // to the approved pill and the gallery refreshes.
                  setRenderRefreshToken((t) => t + 1);
                  const [eRes, mRes] = await Promise.all([
                    getEstimate({ estimateId }),
                    listEstimateMessages({ estimateId }),
                  ]);
                  if (!("error" in eRes)) setEstimate(eRes.data);
                  if (!("error" in mRes)) setMessages(mRes.data);
                }
              }}
              onRejectRender={async (renderId, feedback) => {
                if (!estimateId) return;
                setRenderBusy(true);
                const res = await rejectRender({
                  estimateId,
                  renderId,
                  reason: feedback?.reason,
                  category: feedback?.category,
                });
                setRenderBusy(false);
                if (!("error" in res)) {
                  setRenderRefreshToken((t) => t + 1);
                  const mRes = await listEstimateMessages({ estimateId });
                  if (!("error" in mRes)) setMessages(mRes.data);
                }
              }}
              onApproveDesign={async (designId) => {
                if (!estimateId) return;
                setDesignBusy(true);
                const res = await approveDesign({ estimateId, designId });
                setDesignBusy(false);
                if (!("error" in res)) {
                  const mRes = await listEstimateMessages({ estimateId });
                  if (!("error" in mRes)) setMessages(mRes.data);
                }
              }}
              onRejectDesign={async (designId) => {
                if (!estimateId) return;
                setDesignBusy(true);
                const res = await rejectDesign({ estimateId, designId });
                setDesignBusy(false);
                if (!("error" in res)) {
                  const mRes = await listEstimateMessages({ estimateId });
                  if (!("error" in mRes)) setMessages(mRes.data);
                }
              }}
              designBusy={designBusy}
              onOpenLightbox={(src, alt) => setLightbox({ src, alt })}
              onPhotoUploaded={(photo) => {
                // Inline [[ADD_PHOTO]] upload — refresh the workspace photo
                // list so the gallery + render flow see the new photo.
                photosState.prepend(photo);
              }}
            />
          )}
        />
        <Lightbox
          open={lightbox !== null}
          src={lightbox?.src}
          alt={lightbox?.alt}
          onClose={() => setLightbox(null)}
        />
        <WorkspacePanel>{workspaceCards}</WorkspacePanel>
      </div>

      <MobileWorkspaceDrawer
        summary={{
          photoCount,
          anchorCount,
          snapshot: snapshotPayload,
        }}
      >
        {workspaceCards}
      </MobileWorkspaceDrawer>

      <p className="mt-6 text-xs text-warm-gray">
        <Link to={isAdminView ? "/admin/queue" : "/dashboard"} className="underline">
          ← Back to {isAdminView ? "queue" : "dashboard"}
        </Link>
      </p>
    </div>
  );
}

/**
 * Reconstruct collapsed (single-card) pickers from saved selections so
 * the customer's reload preserves the visual choice. Phase 15 polish:
 * the assistant ChatMessage now persists its `options_payload`, so we
 * also rehydrate pickers the customer was OFFERED but hadn't chosen
 * yet at refresh time. Without that, the catalog cards vanished on
 * reload — the bug surfaced by a customer in May 2026.
 *
 * Each picker is anchored at the assistant message that raised it
 * (the message that carries the options_payload). Preselection comes
 * from any MaterialSelection whose `material_id` matches a card —
 * making the picker collapse to the chosen card with no re-tap needed.
 */
function buildInitialPickers(
  selections: MaterialSelection[],
  messages: ChatMessage[],
): PickerState[] {
  // Group selections by option_group_id so we can preselect cards by
  // matching material_id. Newest selection wins per group.
  const selectionByGroup = new Map<string, MaterialSelection>();
  for (const sel of [...selections].sort((a, b) => a.selected_at.localeCompare(b.selected_at))) {
    selectionByGroup.set(sel.option_group_id, sel);
  }

  const pickers: PickerState[] = [];
  messages.forEach((msg, idx) => {
    if (!msg.options_payload) return;
    // Phase 17 — `intent_picker` messages also carry `options_payload`,
    // but they render via `IntentPicker` in `ChatMessage` directly.
    // Skip them here to avoid a duplicate OptionsCardGrid render.
    if (msg.kind === "intent_picker") return;
    const payload = msg.options_payload;
    const selectionForGroup = selectionByGroup.get(payload.group_id);
    const matchedCard = selectionForGroup
      ? (payload.cards.find((c) => c.option_id === selectionForGroup.material_id) ?? null)
      : null;
    pickers.push({
      payload,
      afterMessageIndex: idx,
      selectedCard: matchedCard,
    });
  });
  return pickers;
}
